import React from 'react';
import cn from 'classnames';

export interface TextLinkProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Primary: light background (e.g. auth cards). Inverse: dark background. */
  theme?: 'primary' | 'inverse';
  size?: 'small' | 'medium';
  /** Icon before the label */
  leftIcon?: React.FC<{ className?: string }>;
  /** Icon after the label */
  rightIcon?: React.FC<{ className?: string }>;
  /** Show only the icon (right or left). Use with rightIcon or leftIcon and aria-label. */
  iconOnly?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const TextLink: React.FC<TextLinkProps> = ({
  theme = 'primary',
  size = 'medium',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  iconOnly = false,
  children,
  className,
  disabled = false,
  type = 'button',
  ...props
}) => {
  const IconComponent = RightIcon ?? LeftIcon;

  const baseClasses = cn(
    'txt-link inline-flex items-center justify-center gap-2',
    'font-ds-600 transition-all duration-200',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-400 focus-visible:ring-offset-2 rounded',
    {
      // Size
      'text-ds-text-s': size === 'small',
      'text-ds-text': size === 'medium',

      // Primary theme (light background): default text-3, hover text-1, pressed #012641, no underline
      'text-ds-text-3 no-underline': theme === 'primary' && !disabled,
      'hover:text-ds-text-1': theme === 'primary' && !disabled,
      'active:text-ds-text-1': theme === 'primary' && !disabled,

      // Inverse theme (dark background): default inverse-3, hover inverse-1, pressed inverse-1, no underline
      'text-ds-text-inverse-3 no-underline': theme === 'inverse' && !disabled,
      'hover:text-ds-text-inverse-1': theme === 'inverse' && !disabled,
      'active:text-ds-text-inverse-1': theme === 'inverse' && !disabled,

      'cursor-not-allowed': disabled,
      'cursor-pointer': !disabled,
    },
    className,
  );

  const iconClasses = cn('shrink-0', {
    'h-4 w-4': size === 'small',
    'h-5 w-5': size === 'medium',
  });

  return (
    <button
      type={type}
      className={baseClasses}
      disabled={disabled}
      aria-disabled={disabled}
      {...props}
    >
      {iconOnly && IconComponent ? (
        <IconComponent className={iconClasses} />
      ) : (
        <>
          {LeftIcon && <LeftIcon className={iconClasses} />}
          {children}
          {RightIcon && <RightIcon className={iconClasses} />}
        </>
      )}
    </button>
  );
};

export default TextLink;
